// utils/datetime.js
// Helpers for converting timestamp strings returned by the backend into
// Date objects that render correctly in the browser's local timezone.
//
// Background:
//   The backend stores `created_at`, `time_entered`, etc. using
//   `datetime.utcnow()` — a *naive* UTC value. FastAPI/Pydantic then
//   serialize it as an ISO string WITHOUT any timezone suffix, e.g.
//   "2026-04-22T08:17:00". `new Date(...)` applied to a TZ-less string
//   interprets it as LOCAL time, so a reading actually taken at 11:17
//   on a UTC+3 machine would render as 08:17. To fix this we force
//   backend timestamps to be parsed as UTC (append "Z") before handing
//   them to `new Date`.

// Accept common timezone suffixes:
// - Z
// - +03 / -05
// - +0300 / -0530
// - +03:00 / -05:30
const TZ_SUFFIX_REGEX = /(Z|[+-]\d{2}(?::?\d{2})?)$/;

/**
 * Parse a timestamp returned by the backend into a Date object.
 * Accepts Date instances, numbers (epoch ms), and ISO strings.
 * If the string lacks an explicit timezone, it is assumed to be UTC.
 *
 * Returns `null` for falsy input or invalid dates.
 */
export const parseBackendDate = (value) => {
    if (value === null || value === undefined || value === '') return null;

    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }

    if (typeof value !== 'string') return null;

    let iso = value.trim();
    if (!iso) return null;

    // Replace " " with "T" (e.g. "2026-04-22 08:17:00" -> "2026-04-22T08:17:00")
    iso = iso.replace(' ', 'T');

    // If no TZ info, assume UTC
    if (!TZ_SUFFIX_REGEX.test(iso)) {
        iso += 'Z';
    }

    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
};
